import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { useToast } from "@/components/ui/Feedback";
import { ConfirmDialog } from "@/components/ui/Modal";
import { api, type ApiError } from "@/lib/api";
import { todayIn } from "@/lib/format";
import { clearLastUser, saveLastUser } from "@/lib/lastUser";
import { isNetworkError, keys, useMe } from "@/lib/queries";
import type { User } from "@/lib/types";
import { currentDatabase, destroyLocalDatabase } from "@/local/database";
import { outboxSummary, type OutboxSummary } from "@/local/outbox";
import { requestSync } from "@/local/sync";

interface AuthResult {
  user: User;
  access_expires_at: string;
}

interface AuthState {
  user: User | null;
  loading: boolean;
  today: string;
  login: (email: string, password: string) => Promise<void>;
  register: (input: { email: string; password: string; full_name: string }) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

/**
 * Switch the signed-in identity. Every other cached query belongs to the previous user and is
 * dropped. The "me" query itself is updated in place: clearing it would detach the mounted
 * observer, which would keep showing the stale signed-out state.
 */
function switchUser(qc: QueryClient, user: User | null) {
  qc.removeQueries({ predicate: (q) => q.queryKey[0] !== keys.me[0] });
  qc.setQueryData(keys.me, user);
}

/**
 * The server said this session is no longer valid (any 401 that survived a token refresh).
 * Always shows the sign-in page. Only a *revoked* session - the user or an admin ended it, e.g.
 * because the phone was lost - also erases this device's copy of their data: an expired session
 * just needs a fresh sign-in, and their data is still theirs.
 */
export function handleAuthFailure(qc: QueryClient, error: ApiError): void {
  const userId = qc.getQueryData<User>(keys.me)?.id;
  qc.setQueryData(keys.me, null);
  if (userId && (error.code === "SESSION_REVOKED" || error.code === "REFRESH_REUSED")) {
    clearLastUser();
    void destroyLocalDatabase(userId).catch(() => undefined);
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const me = useMe();
  const user = me.data ?? null;
  const loading = me.isPending;

  const login = useCallback(async (email: string, password: string) => {
    const result = await api.post<AuthResult>("/auth/login", { email, password });
    saveLastUser(result.user);
    switchUser(qc, result.user);
  }, [qc]);

  const register = useCallback(async (input: { email: string; password: string; full_name: string }) => {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "Africa/Nairobi";
    const result = await api.post<AuthResult>("/auth/register", { ...input, timezone });
    saveLastUser(result.user);
    switchUser(qc, result.user);
  }, [qc]);

  const toast = useToast();
  const [unsynced, setUnsynced] = useState<OutboxSummary | null>(null);
  const [leaving, setLeaving] = useState(false);

  // Only the server can clear the httpOnly session cookies. Offline, "signing out" would erase
  // the local data yet leave the session alive - the next visit would silently sign back in.
  // Refuse, and say why.
  const offlineToast = useCallback(
    () => toast("You're offline. Signing out needs a connection so your session ends properly.", "error"),
    [toast],
  );

  /** Ends the session and erases this device's copy of the user's data. */
  const signOutNow = useCallback(async () => {
    const userId = qc.getQueryData<User>(keys.me)?.id;
    try {
      await api.post("/auth/logout");
    } catch (error) {
      if (isNetworkError(error)) return offlineToast();
      // Any other answer (e.g. the session had already ended) still means "signed out".
    }
    if (userId) await destroyLocalDatabase(userId).catch(() => undefined);
    clearLastUser();
    switchUser(qc, null);
  }, [qc, offlineToast]);

  const logout = useCallback(async () => {
    if (typeof navigator !== "undefined" && !navigator.onLine) return offlineToast();

    // Signing out erases this device's copy of the data. Anything the user recorded here that the
    // server has not taken yet would go with it, so: one last attempt to send it, and if any is
    // still waiting, ask - never lose it silently.
    const database = currentDatabase();
    if (database) {
      await requestSync();
      const summary = await outboxSummary(database.driver);
      if (summary.waiting + summary.attention > 0) return setUnsynced(summary);
    }
    await signOutNow();
  }, [offlineToast, signOutNow]);

  const value = useMemo<AuthState>(() => ({
    user,
    loading,
    today: todayIn(user?.timezone ?? "Africa/Nairobi"),
    login,
    register,
    logout,
  }), [user, loading, login, register, logout]);

  const changes = (n: number) => `${n} change${n === 1 ? "" : "s"}`;
  const details = unsynced
    ? [
        unsynced.waiting > 0 ? `${changes(unsynced.waiting)} not yet sent to the server` : null,
        unsynced.attention > 0 ? `${changes(unsynced.attention)} the server didn't accept, which you haven't reviewed` : null,
      ].filter(Boolean).join(" and ")
    : "";

  return (
    <AuthContext.Provider value={value}>
      {children}
      <ConfirmDialog
        open={unsynced !== null}
        title="Sign out with unsynced changes?"
        message={`This device still holds ${details}. Signing out erases this device's copy of your data, so they would be lost. Stay signed in and anything waiting will be sent as soon as the connection allows.`}
        confirmLabel="Sign out anyway"
        busy={leaving}
        onClose={() => setUnsynced(null)}
        onConfirm={() => {
          setLeaving(true);
          void signOutNow().finally(() => {
            setLeaving(false);
            setUnsynced(null);
          });
        }}
      />
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth outside AuthProvider");
  return ctx;
}

/** For screens that only render when signed in. */
export function useUser(): User {
  const { user } = useAuth();
  if (!user) throw new Error("useUser without a signed-in user");
  return user;
}
